import { expose } from 'comlink';
import { collectionGroup, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, query, Query, startAfter, where } from 'firebase/firestore';
import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { IdbIssueMedia, IssueMedia } from '../../issue/issue.dto';
import { merge } from 'lodash-es';
import { DocumentData, QueryConstraint } from '@angular/fire/firestore';
import { dtoConverter, ISSUE_MEDIAS_STORE, MEDIAS_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseIssuesMediasStepsWorker extends AbstractWarehouseDataSyncStepWorker<IdbIssueMedia> {
  constructor() {
    super(ISSUE_MEDIAS_STORE, 'ISSUES MEDIAS');
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: IdbIssueMedia[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    const q: Query = query(collectionGroup(getFirestore(), MEDIAS_COLLECTION), where('division._id', '==', ctx.division), orderBy('issue._id'), ...queryConstraints).withConverter(
      dtoConverter({ syncId: ctx.syncId }),
    );
    const queryResult = await getDocs<IssueMedia, DocumentData>(q);

    if (queryResult.docs.length && !this.shouldStopTask) {
      const medias: IssueMedia[] = queryResult.docs.map((d) => d.data());

      logger.info('Downloaded issues medias from Firestore', { count: medias.length, category: 'sync' });

      const tx = await this.idbService.createTransaction(this.store);
      const objectStore = tx.objectStore(this.store);
      const pending: Promise<unknown>[] = [];

      for (const i of medias) {
        let matching = await objectStore.get([ctx.division, i._id]);
        if (matching) {
          merge(matching, i);
        } else {
          matching = i;
        }

        pending.push(objectStore.put(matching));
      }

      await this.idbService.commitTransaction(tx, pending, { store: this.store, division: ctx.division, chunk: queryResult.docs.length });
      return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
    }

    return [null, 0];
  }

  protected updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    onProgress(0, { processed: downloaded });
    return Promise.resolve(undefined);
  }
}

expose(WarehouseIssuesMediasStepsWorker);
